<script setup>
// The who-can-book table (BOOKINGS_PLAN.md B4b; canvas BookRules): one row
// each for No membership, every membership type, Visitors and Guests ×
// Can book, Longest booking, Book up to, Bookings at once and Price a slot.
// An empty cell copies the No membership row and shows its value greyed;
// clear a cell to copy again. Empty price = free on the No membership row;
// on the others it copies, and 0 means free.
import {computed, ref, watch} from 'vue';
import {
  AT_ONCE_OPTIONS, AUDIENCE, DAYS_AHEAD_OPTIONS, audienceHelp, cellApplies, cellValue, durationOptions, durationWord,
  rowsToRequest, tableRows,
} from '~/utils/bookings';
import {minorToMajor} from '~/utils/memberships';
import MoneyField from '~/components/admin/memberships/MoneyField.vue';

const props = defineProps({
  group: {type: Object, required: true},
  // Live membership types [{id, name, currentCount}].
  membershipTypes: {type: Array, default: () => []},
  currency: {type: String, default: 'gbp'},
  paymentsReady: {type: Boolean, default: false},
  // X137 (R66 M2): while off, a price is a draft (the switch-on asks for payments); on, the save itself does.
  bookingsOn: {type: Boolean, default: false},
  // Type id → status label ("Draft", "Live", "Closed to new"), for the line under a type's name.
  typeStatuses: {type: Object, default: () => ({})},
});
// X96: 'setupPayments' opens the payments dialog on the page, beside the warning (act in place).
const emit = defineEmits(['update:dirty', 'setupPayments']);

const build = () => tableRows(props.group?.whoCanBook ?? [], props.membershipTypes);
const rows = ref(build());
const baseline = ref(JSON.stringify(rowsToRequest(rows.value)));
const dirty = computed(() => JSON.stringify(rowsToRequest(rows.value)) !== baseline.value);
watch(() => [props.group?.version, props.membershipTypes.length], ([, n], [, on]) => {
  // X71 (X60's finding): the hours painter's own Save bumps the group's version, and rebuilding here threw away an
  // edit not yet saved. While the table is dirty the rows stay and only the baseline moves to the fresh group, so
  // Save still knows what differs (a stale version is still refused by the API as before). A change in the types
  // themselves (they load after the page) rebuilds as before, so every type gets its row.
  if (dirty.value && n === on) {
    baseline.value = JSON.stringify(rowsToRequest(build()));
    return;
  }
  rows.value = build();
  baseline.value = JSON.stringify(rowsToRequest(rows.value));
});
watch(dirty, (v) => emit('update:dirty', v), {immediate: true});

const none = computed(() => rows.value.find(r => r.audience === AUDIENCE.noMembership));
const durations = computed(() => durationOptions(props.group?.slotMinutes ?? 60));
const counts = computed(() => Object.fromEntries(props.membershipTypes.map(t => [t.id, t.currentCount])));

const isNone = (row) => row.audience === AUDIENCE.noMembership;
const inherited = (row, field) => !isNone(row) && cellValue(row, field, none.value).inherited;
const shown = (row, field) => cellValue(row, field, none.value).value;
const titleOf = (options, value) => options.find(o => o.value === value)?.title ?? '';
const placeholderFor = (row, field, options) => {
  if (isNone(row)) return field === 'maxActiveBookings' ? 'Any' : '';
  const v = shown(row, field);
  if (field === 'maxDuration') return v == null ? '' : titleOf(options, v);
  if (field === 'daysAhead') return titleOf(options, v);
  if (field === 'maxActiveBookings') return v == null ? 'Any' : titleOf(options, v);
  return '';
};
const canBookShown = (row) => {
  const own = row.canBook;
  if (own != null || isNone(row)) return !!own;
  return !!none.value?.canBook;
};
const setCanBook = (row, v) => { row.canBook = v; };

// Price: null (an empty box) = copy (or free on No membership), 0 = free, otherwise minor units. The box is the
// shared MoneyField with zero-is-price, so a saved £6 reads "6.00" and a typed "0" settles as "0.00" (X62).
const setPrice = (row, minor) => { row.priceMinor = minor; };
const pricePlaceholder = (row) => {
  if (isNone(row)) return 'Free';
  const v = shown(row, 'priceMinor');
  return v ? minorToMajor(v) : 'Free';
};

// X137 (R66 M2): a price the club can't take yet (no payment account) gets one calm line under the box, on every
// priced row, not only Visitors; the fix is beside it. Shown from the first keystroke, not after a failed Save.
const priceNeedsPayments = (row) => !props.paymentsReady && (row.priceMinor ?? 0) > 0;
const needsPaymentsLine = computed(() => (props.bookingsOn ? "Players can't pay this yet: set up payments first." : "Players can't pay yet: set up payments before switching bookings on."));

const request = () => rowsToRequest(rows.value);
defineExpose({request, dirty});
</script>

<template>
  <div class="wcb">
    <div class="tl-table-wrap">
      <table class="tl-table wcb-table">
        <thead>
          <tr>
            <th>Who</th>
            <th>Can book</th>
            <th>Longest booking</th>
            <th>Book up to</th>
            <th>Bookings at once</th>
            <th>Price a slot</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.audience + (row.membershipTypeId || '')" :class="{'wcb-row--off': !canBookShown(row)}">
            <td>
              <div class="tl-cell-title">{{ row.label }}</div>
              <div class="tl-cell-sub">{{ audienceHelp(row, counts, typeStatuses) }}</div>
            </td>
            <td>
              <v-checkbox-btn :model-value="canBookShown(row)" color="primary" :class="{'wcb-inherited': inherited(row, 'canBook')}" :aria-label="`${row.label} can book`" @update:model-value="setCanBook(row, $event)"/>
            </td>
            <td>
              <v-select
                  v-if="cellApplies(row, 'maxDuration')"
                  v-model="row.maxDuration"
                  :items="durations"
                  item-value="value"
                  item-title="title"
                  :placeholder="placeholderFor(row, 'maxDuration', durations)"
                  :clearable="!isNone(row) && row.maxDuration != null"
                  :class="{'wcb-inherited': inherited(row, 'maxDuration')}"
                  density="compact"
                  hide-details
              />
              <span v-else class="wcb-dash">—</span>
            </td>
            <td>
              <v-select
                  v-if="cellApplies(row, 'daysAhead')"
                  v-model="row.daysAhead"
                  :items="DAYS_AHEAD_OPTIONS"
                  item-value="value"
                  item-title="title"
                  :placeholder="placeholderFor(row, 'daysAhead', DAYS_AHEAD_OPTIONS)"
                  :clearable="!isNone(row) && row.daysAhead != null"
                  :class="{'wcb-inherited': inherited(row, 'daysAhead')}"
                  density="compact"
                  hide-details
              />
              <span v-else class="wcb-dash">—</span>
            </td>
            <td>
              <v-select
                  v-if="cellApplies(row, 'maxActiveBookings')"
                  v-model="row.maxActiveBookings"
                  :items="AT_ONCE_OPTIONS"
                  item-value="value"
                  item-title="title"
                  :placeholder="placeholderFor(row, 'maxActiveBookings', AT_ONCE_OPTIONS)"
                  :clearable="!isNone(row) && row.maxActiveBookings != null"
                  :class="{'wcb-inherited': inherited(row, 'maxActiveBookings')}"
                  density="compact"
                  hide-details
              />
              <span v-else-if="row.audience === AUDIENCE.visitor" class="wcb-fixed">1</span>
              <span v-else class="wcb-dash">—</span>
            </td>
            <td>
              <money-field
                  :model-value="row.priceMinor"
                  :currency="currency"
                  zero-is-price
                  :placeholder="pricePlaceholder(row)"
                  :class="{'wcb-inherited': inherited(row, 'priceMinor')}"
                  density="compact"
                  hide-details
                  @update:model-value="setPrice(row, $event)"
              />
              <div v-if="priceNeedsPayments(row)" class="wcb-pay">{{ needsPaymentsLine }} <a href="#" role="button" @click.prevent="emit('setupPayments')">Set up payments</a></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="wcb-note">
      Grey values copy the <b>No membership</b> row (clear a cell to copy again); an empty price is free. Guests are added to a booking by name,
      so only Can book and the fee a guest apply. Admins can always go over.
    </div>
  </div>
</template>

<style scoped lang="scss">
.wcb-table {
  td {
    vertical-align: middle;
  }

  th:not(:first-child) {
    white-space: nowrap;
  }

  td:not(:first-child) {
    min-width: 136px;
  }

  /* The selects never shorten their words ("7 da…"). */
  :deep(.v-select .v-field__input),
  :deep(.v-select .v-select__selection-text) {
    white-space: nowrap;
    overflow: visible;
  }

  td:first-child {
    min-width: 150px;
  }
}

.wcb-row--off {
  td:not(:first-child):not(:nth-child(2)) {
    opacity: 0.45;
  }
}

.wcb-inherited {
  :deep(input::placeholder),
  :deep(.v-field__input) {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 1;
  }

  :deep(.v-field) {
    background: rgb(var(--v-theme-light));
  }
}

.wcb-dash {
  color: rgb(var(--v-theme-textSubtle));
  padding-left: 14px;
}

.wcb-fixed {
  padding-left: 14px;
  font-size: 13px;
}

/* X137: the no-payments-yet line under a price: quiet, with its fix */
.wcb-pay {
  font-size: 11px;
  line-height: 1.4;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 3px;
  white-space: normal;

  a {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
    text-decoration: none;
  }
}

.wcb-note {
  font-size: 12px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 10px;
}
</style>
